import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router';
import { Space, Table, Tag, Typography } from 'antd';
import type { TableProps } from 'antd';
import type { CaptureType, Lead } from '@viq-admin/types';
import { AsyncBoundary, formatDateTime, space } from '@viq-admin/ui';
import { listLeads } from '../api/leads';
import { useConferenceNameMap } from '../lookups/useLookups';
import { leadsHref, type LeadsUrlFilter } from '../views/Leads/leadsHref';
import { CompanyLink, ConferenceLink, LeadLink, UserLink } from './EntityLinks';

/**
 * The latest few leads for one company, rep or conference, newest first,
 * with a "See all" link into the full Leads list carrying the same filter.
 * Company, user and conference pages embed it so "what have they captured
 * lately" needs no navigation.
 *
 * The filter is always explicit (companyId / repOid / conferenceId), never the
 * global "Operate as" scope, so the table shows the same rows whatever scope
 * the operator happens to have set. Read-only; `read Lead` gates the backend
 * route, and every console role has it.
 */

const CAPTURE_COLOR: Record<CaptureType, string> = {
  voice: 'purple',
  badge: 'blue',
  card: 'green',
};

export interface LeadsMiniTableProps {
  filter: LeadsUrlFilter;
  /** Rows to show. Default 10. */
  limit?: number;
  /** Hide columns the page already implies (e.g. Company on a company page). */
  hide?: Array<'company' | 'rep' | 'conference'>;
}

export function LeadsMiniTable({ filter, limit = 10, hide = [] }: LeadsMiniTableProps) {
  const conferenceNames = useConferenceNameMap();
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['leadsMini', filter, limit],
    queryFn: () => listLeads({ ...filter, limit }),
  });
  const leads = data?.leads ?? [];

  const columns: NonNullable<TableProps<Lead>['columns']> = [
    {
      key: 'name',
      title: 'Lead',
      render: (_: unknown, r: Lead) => <LeadLink id={r.id} name={r.preview} />,
    },
    ...(hide.includes('company')
      ? []
      : [
          {
            key: 'company',
            title: 'Company',
            render: (_: unknown, r: Lead) => <CompanyLink id={r.companyId} />,
          },
        ]),
    ...(hide.includes('rep')
      ? []
      : [
          {
            key: 'rep',
            title: 'Rep',
            render: (_: unknown, r: Lead) => <UserLink oid={r.repOid} name={r.repName} />,
          },
        ]),
    ...(hide.includes('conference')
      ? []
      : [
          {
            key: 'conference',
            title: 'Conference',
            render: (_: unknown, r: Lead) => (
              <ConferenceLink id={r.conferenceId} name={conferenceNames.get(r.conferenceId)} />
            ),
          },
        ]),
    {
      key: 'type',
      title: 'Type',
      width: 90,
      render: (_: unknown, r: Lead) =>
        r.captureType ? <Tag color={CAPTURE_COLOR[r.captureType]}>{r.captureType}</Tag> : null,
    },
    {
      key: 'captured',
      title: 'Captured',
      width: 190,
      render: (_: unknown, r: Lead) => formatDateTime(r.createdAt),
    },
  ];

  return (
    <AsyncBoundary error={error} onRetry={() => void refetch()}>
      <Space direction="vertical" size={space.sm} style={{ width: '100%' }}>
        <Table<Lead>
          rowKey="id"
          size="small"
          dataSource={leads}
          columns={columns}
          loading={isLoading}
          pagination={false}
          scroll={{ x: 'max-content' }}
          locale={{ emptyText: 'No leads yet' }}
        />
        {data?.nextCursor || leads.length > 0 ? (
          <Typography.Text type="secondary">
            <Link to={leadsHref(filter)}>See all leads →</Link>
          </Typography.Text>
        ) : null}
      </Space>
    </AsyncBoundary>
  );
}
